import { PropsWithChildren } from 'react';
import styled, {
  css,
  FlattenInterpolation,
  FlattenSimpleInterpolation,
  ThemedStyledProps,
} from 'styled-components';
import tw from 'tailwind.macro';
import { fadeIn } from '../../../animations/keyframes';
import { slideX, slideY } from '../../../animations/modal';
import { screens } from '../../../styles/constants';

const slideYFrom = 10;
const slideYTo = 0;

const slideXFrom = -10;
const slideXTo = 0;

const ANIMATION_DURATION = '350ms ease-in-out';

const Wrapper = styled.div<{
  desktopBreakpoint: string;
  customStyle: FlattenSimpleInterpolation;
}>`
  ${tw`relative flex flex-col w-full h-full shadow-medium  bg-white rounded-30 overflow-hidden
  `}

  ${({ customStyle }) => customStyle}
`;

type StyleProps = { desktopBreakpoint: string };

const emptyStyle = css<StyleProps>``;

const MobileModalNormalStyle = css<StyleProps>`
  ${tw`pt-big `}
  ${Wrapper} {
    ${tw`rounded-bl-none rounded-br-none`}
  }
`;

const MobileModalFullScreenStyle = css<StyleProps>`
  ${Wrapper} {
    ${tw`rounded-none`}
  }
`;

const MobileModalCenteredStyle = css<StyleProps>`
  ${tw`p-medium`}

  ${Wrapper} {
    ${tw`m-auto`}
    max-height: 80vh;
  }
`;

const MobileModalSmallStyle = css<StyleProps>`
  ${tw`p-medium`}

  ${Wrapper} {
    ${tw`m-auto`}
    max-height: 60vh;
  }
`;

const MobileModalNoneStyle = css<StyleProps>`
  ${({ desktopBreakpoint }) => css`
    @media (max-width: ${desktopBreakpoint}) {
      ${tw`static bg-transparent z-0`}

      ${Wrapper} {
        animation: none;
        ${tw`shadow-none m-0 pt-0`}
      }
    }
  `}
`;

const DesktopModalSmallStyle = css<StyleProps>`
  ${Wrapper} {
    max-height: 50vh;
  }
`;

const DesktopModalNoneStyle = css<StyleProps>`
  ${({ desktopBreakpoint }) => css`
    @media (min-width: ${desktopBreakpoint}) {
      ${tw`static bg-transparent z-0`}

      ${Wrapper} {
        animation: none;
        ${tw`shadow-none m-0`}
      }
    }
  `}
`;

const MOBILE_MODAL_VARIANTS_STYLE: Record<
  FunnelModalMobileVariant,
  FlattenInterpolation<ThemedStyledProps<StyleProps, any>>
> = {
  FULL_SCREEN: MobileModalFullScreenStyle,
  NORMAL: MobileModalNormalStyle,
  CENTERED: MobileModalCenteredStyle,
  NONE: MobileModalNoneStyle,
  SMALL: MobileModalSmallStyle,
};

const DESKTOP_MODAL_VARIANTS_STYLE: Record<
  FunnelModalDesktopVariant,
  FlattenInterpolation<ThemedStyledProps<StyleProps, any>>
> = {
  NORMAL: emptyStyle,
  SMALL: DesktopModalSmallStyle,
  NONE: DesktopModalNoneStyle,
  LARGE: css`
    ${Wrapper} {
      max-width: unset;
    }
  `,
};

type ContainerProps = {
  mobileStyle: FlattenInterpolation<ThemedStyledProps<StyleProps, any>>;
  desktopStyle: FlattenInterpolation<ThemedStyledProps<StyleProps, any>>;
  desktopBreakpoint: string;
};

const Container = styled.div<ContainerProps>`
  ${tw`bg-overlay z-5 fixed w-full flex 
      md:bg-overlay md:z-5 md:fixed
  `}

  inset: 0;

  ${({ desktopBreakpoint }) => css`
    ${Wrapper} {
      animation: ${slideY(slideYFrom, slideYTo)} ${ANIMATION_DURATION},
        ${fadeIn} ${ANIMATION_DURATION};

      @media (min-width: ${desktopBreakpoint}) {
        animation: ${slideX(slideXFrom, slideXTo)} ${ANIMATION_DURATION},
          ${fadeIn} ${ANIMATION_DURATION};
        width: 70vw;
        max-width: 513px;
        max-height: 80vh;
        margin: auto;
      }
    }
  `}

  ${({ desktopBreakpoint, mobileStyle }) => css`
    @media (max-width: ${desktopBreakpoint}) {
      ${mobileStyle}
    }
  `}

  ${({ desktopBreakpoint, desktopStyle }) => css`
    @media (min-width: ${desktopBreakpoint}) {
      ${desktopStyle}
    }
  `}
`;

type FunnelModalMobileVariant =
  | 'FULL_SCREEN'
  | 'NORMAL'
  | 'CENTERED'
  | 'NONE'
  | 'SMALL';

type FunnelModalDesktopVariant = 'NORMAL' | 'LARGE' | 'SMALL' | 'NONE';

type FunnelModalProps = PropsWithChildren<{
  mobileVariant?: FunnelModalMobileVariant;
  desktopVariant?: FunnelModalDesktopVariant;
  desktopBreakpoint?: string;
  onBackgroundClick?: () => void;
  isOpen?: boolean;
  wrapperStyle?: FlattenSimpleInterpolation;
}>;

const FunnelModal = ({
  children,
  mobileVariant = 'NORMAL',
  desktopVariant = 'NORMAL',
  desktopBreakpoint = screens.md,
  onBackgroundClick,
  wrapperStyle,
  isOpen = true,
}: FunnelModalProps) => {
  const mobileStyle = MOBILE_MODAL_VARIANTS_STYLE[mobileVariant];
  const desktopStyle = DESKTOP_MODAL_VARIANTS_STYLE[desktopVariant];

  if (!isOpen) {
    return null;
  }

  return (
    <Container
      desktopBreakpoint={desktopBreakpoint}
      desktopStyle={desktopStyle}
      mobileStyle={mobileStyle}
      onClick={onBackgroundClick}
    >
      <Wrapper customStyle={wrapperStyle} desktopBreakpoint={desktopBreakpoint}>
        {children}
      </Wrapper>
    </Container>
  );
};

export default FunnelModal;
